import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color, FontSize, LineHeight, TextStyle } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { classNames } from 'primereact/utils';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { useClient } from '../../hooks/useClient';
import { useWebsiteContext } from '../../context/WebsiteContext';
import { usePopups } from '../../context/PopupContext';
import { Indent } from './extensions/indent';
import { VideoEmbed } from './extensions/videoEmbed';
import { RichTextEditorToolbar } from './RichTextEditorToolbar';
import './RichTextEditor.scss';

export interface UploadImageResponse {
  imageUrl: string;
}

interface Props {
  /**
   * The content the editor opens with. A later change of this prop is ignored: new content goes in through the
   * `setContent` of the ref (#524). A `setContent` call before the editor exists is dropped, so a parent mounts the editor
   * with the content it holds.
   */
  initialValue?: string;
  id: string;
  onChange?: (value: string) => void;
  placeholder: string;
  className?: string;
  hideFontSize?: boolean;
  useBase64Images?: boolean;
  disabled?: boolean;
}

const htmlToPlainText = (html: string): string => new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

const getImageSrcs = (doc: any): Set<string> => {
  const srcs = new Set<string>();
  doc.descendants((node: any) => {
    if (node.type.name === 'image' && node.attrs.src) {
      srcs.add(node.attrs.src);
    }
  });
  return srcs;
};

/** What a parent can call on the editor through its ref. */
export interface RichTextEditorHandle {
  /** Replaces the content without reporting a change. Content equal to the current one is left alone. */
  setContent: (html: string) => void;
}

/**
 * The editor owns its content: the parent reads it through `onChange` and replaces it on purpose through `setContent`
 * (a post loads, the locale changes, the form resets). A parent that stores the reported value late can no longer put an
 * older value back over newer edits (#524).
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  { initialValue, id, onChange, placeholder, className, hideFontSize = false, useBase64Images = false, disabled = false },
  ref,
) {
  const { showToast } = usePopups();
  const { website } = useWebsiteContext();
  const client = useClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousImageSrcsRef = useRef<Set<string>>(new Set());
  const editorRef = useRef<Editor | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isNativePlatform = Capacitor.isNativePlatform();

  // After an image the selection is the image itself, so a next image would replace it. When several files are dropped
  // together, each image leaves an empty paragraph with the cursor, where the next one goes.
  const insertImageSrc = (src: string, moreToCome: boolean) => {
    const chain = editorRef.current?.chain().focus().setImage({ src });
    (moreToCome ? chain?.createParagraphNear() : chain)?.run();
  };

  // The address of the image: its data url in base64 mode, else the address of its upload. Undefined when it fails.
  const loadImageSrc = async (file: File): Promise<string | undefined> => {
    if (useBase64Images) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(file);
      });
    }

    const formData = new FormData();
    formData.append('image', file);
    const response = await client.post<UploadImageResponse>(`/websites/${website?.id}/upload`, {
      body: formData,
      errorMessages: {
        summary: 'Error al subir la imagen',
        defaultDetail: 'No se ha podido subir la imagen. Inténtalo de nuevo.',
      },
    });
    return response?.imageUrl;
  };

  const insertImageFile = async (file: File) => {
    const src = await loadImageSrc(file);
    if (src) insertImageSrc(src, false);
  };

  const insertImageUrl = (url: string) => {
    editorRef.current?.chain().focus().setImage({ src: url }).run();
  };

  const handleImagesRemoved = (removedSrcs: string[]) => {
    if (useBase64Images || !removedSrcs.length) return;
    // One request per image: the backend rejects the whole batch when one URL is not in the bucket (#258).
    removedSrcs.forEach((src) => {
      client.post(`/websites/${website?.id}/delete-images`, {
        body: { imageUrls: [src] },
      });
    });
  };

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    editable: !disabled,
    content: initialValue,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      TextStyle,
      Color,
      FontSize,
      LineHeight.configure({ types: ['heading', 'paragraph'] }),
      Indent,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      // allowBase64 is required so that base64 image srcs (useBase64Images mode) survive
      // being re-parsed from HTML when the editor reloads previously saved content;
      // otherwise Tiptap's default parseHTML rule silently drops `data:` image sources.
      Image.configure({ allowBase64: true }),
      VideoEmbed,
      // Column resizing relies on mousedown/mousemove, which don't fire reliably on
      // touch drag (iPad), so the resize handles are disabled on native platforms.
      Table.configure({ resizable: !isNativePlatform }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    editorProps: {
      attributes: { id },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) return false;

        const hasNonImageFile = files.some((file) => !file.type.startsWith('image/'));
        if (hasNonImageFile) {
          event.preventDefault();
          showToast?.({
            severity: 'error',
            summary: 'Archivo inválido',
            detail: 'Solo se pueden subir imágenes',
          });
          return true;
        }

        event.preventDefault();
        // Every file loads at once, and the images go in in the order of the files, whichever ends first (#374).
        // Each image goes in as soon as it and the images before it are done, so a stalled upload holds back only the ones after it.
        // The catch goes on each load when it starts, so a later load that fails first is never an unhandled rejection.
        const loads = files.map((file) => loadImageSrc(file).catch(() => undefined));
        void (async () => {
          for (const load of loads) {
            const src = await load;
            if (src) insertImageSrc(src, files.length > 1);
          }
        })();
        return true;
      },
      handlePaste: (_view, event) => {
        // Handles pasting a raw image (screenshot, copied image file) regardless of
        // upload mode — matches Froala's `pasteImage` behavior, which uploaded pasted
        // images to the server just like the upload button when not using base64.
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((item) => item.type.indexOf('image') === 0);
        if (imageItem) {
          const blob = imageItem.getAsFile();
          if (blob) {
            event.preventDefault();
            insertImageFile(blob);
            return true;
          }
        }

        // Always paste as plain text (matches Froala's pastePlain: true), regardless of
        // source (Word, Google Docs, Notes/Pages on iOS, etc.). Some clipboard sources
        // (notably some iOS copy flows) omit text/plain and only provide text/html, so
        // that's used as a fallback to strip formatting rather than letting it fall
        // through to Tiptap's default HTML paste handling.
        const plainText = event.clipboardData?.getData('text/plain');
        const html = event.clipboardData?.getData('text/html');
        const text = plainText || (html ? htmlToPlainText(html) : undefined);
        if (text) {
          event.preventDefault();
          editorRef.current?.commands.insertContent(text.replace(/\n/g, '<br>'));
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      const html = updatedEditor.getHTML();

      const currentImageSrcs = getImageSrcs(updatedEditor.state.doc);
      const removedSrcs = [...previousImageSrcsRef.current].filter((src) => !currentImageSrcs.has(src));
      previousImageSrcsRef.current = currentImageSrcs;
      handleImagesRemoved(removedSrcs);

      onChange?.(html);
    },
    onCreate: ({ editor: createdEditor }) => {
      previousImageSrcsRef.current = getImageSrcs(createdEditor.state.doc);
    },
  });

  editorRef.current = editor ?? null;

  useImperativeHandle(
    ref,
    () => ({
      setContent: (html: string) => {
        const current = editorRef.current;
        if (!current || html === current.getHTML()) return;
        current.commands.setContent(html, { emitUpdate: false });
        previousImageSrcsRef.current = getImageSrcs(current.state.doc);
      },
    }),
    [],
  );

  useEffect(() => {
    // Not a content change: Tiptap emits an update by default, which reported the html of an editor that had not yet
    // received its value, and the form kept that over the loaded content (#124).
    editor?.setEditable(!disabled, false);
  }, [disabled, editor]);

  useEffect(() => {
    if (!isNativePlatform || !editor) return;

    // WKWebView (iOS) resizes the whole webview when the keyboard appears, but ProseMirror's
    // own scroll-into-view can run before that resize settles, leaving the caret hidden behind
    // the keyboard. Re-running it once the keyboard has fully shown fixes that on iPad.
    const listenerPromise = Keyboard.addListener('keyboardDidShow', () => {
      if (editor.isFocused) editor.commands.scrollIntoView();
    });

    return () => {
      listenerPromise.then((listener) => listener.remove());
    };
  }, [isNativePlatform, editor]);

  if (!editor) return null;

  return (
    <div className={classNames('rich-text-editor', className, { fullscreen: isFullscreen })}>
      <RichTextEditorToolbar
        editor={editor}
        hideFontSize={hideFontSize}
        disabled={disabled}
        isFullscreen={isFullscreen}
        showFullscreenButton={!isNativePlatform}
        onToggleFullscreen={() => setIsFullscreen((current) => !current)}
        onRequestImageUpload={() => fileInputRef.current?.click()}
        onInsertImageUrl={insertImageUrl}
      />
      <EditorContent editor={editor} className='rich-text-content' />
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) insertImageFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
});
